import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Security,
  Warning,
  Assessment,
  Timeline,
  ShowChart,
  BarChart,
  OpenInNew,
  Business,
} from '@mui/icons-material';
import { 
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  ResponsiveContainer, BarChart as RechartsBarChart, Bar, 
  PieChart as RechartsPieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { useSnackbar } from 'notistack';
import api from '../services/api';
import { LoggerService } from '../services/LoggerService';

interface RiskCategory {
  id: string;
  name: string;
  description: string;
  risk_type: string;
  severity_levels: string[];
}

interface RiskFactor {
  id: string;
  category_id: string;
  name: string;
  description: string;
  factor_type: string;
  measurement_unit: string;
  weight: number;
}

interface FundRiskProfile {
  id: string;
  fund_id: string;
  risk_score: number;
  risk_level: string;
  assessment_date: string;
  assessment_methodology: string;
  assessment_frequency: string;
}

interface RiskScenario {
  id: string;
  name: string;
  description: string;
  scenario_type: string;
  severity: string;
  probability: number;
  impact_factors: any;
  assumptions: string;
}

interface RiskLimit {
  id: string;
  fund_id: string;
  risk_factor_id: string;
  limit_type: string;
  limit_value: number;
  limit_unit: string;
  breach_action: string;
  effective_date: string;
  risk_factor?: RiskFactor;
}

interface RiskBreach {
  id: string;
  fund_id: string;
  risk_limit_id: string;
  breach_date: string;
  breach_value: number;
  limit_value: number;
  breach_severity: string;
  breach_description: string;
  action_taken: string;
  resolved_date?: string;
  is_resolved: boolean;
}

interface RiskReport {
  id: string;
  fund_id: string;
  report_name: string;
  report_type: string;
  report_date: string;
  executive_summary: string;
  key_findings: any[];
  recommendations: any[];
  risk_metrics: any;
  report_status: string;
}

interface Fund {
  id: string;
  name: string;
  legal_name: string;
  lei: string;
  fund_type: string;
  total_assets: number;
  currency: string;
  status: string;
  risk_level: string;
}

const RiskAnalysisPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  // Data states
  const [categories, setCategories] = useState<RiskCategory[]>([]);
  const [factors, setFactors] = useState<RiskFactor[]>([]);
  const [profiles, setProfiles] = useState<FundRiskProfile[]>([]);
  const [scenarios, setScenarios] = useState<RiskScenario[]>([]);
  const [limits, setLimits] = useState<RiskLimit[]>([]);
  const [breaches, setBreaches] = useState<RiskBreach[]>([]);
  const [reports, setReports] = useState<RiskReport[]>([]);
  const [funds, setFunds] = useState<Fund[]>([]);
  const [dashboardSummary, setDashboardSummary] = useState<any>(null);

  // Dialog states
  const [selectedFund, setSelectedFund] = useState<Fund | null>(null);
  const [fundAnalysisDialogOpen, setFundAnalysisDialogOpen] = useState(false);
  const [fundAnalysisData, setFundAnalysisData] = useState<any>(null);

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('');

  // Load all risk analysis data
  useEffect(() => {
    loadRiskAnalysisData();
  }, []);

  const loadRiskAnalysisData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel
      const [
        categoriesRes,
        factorsRes,
        profilesRes,
        scenariosRes,
        limitsRes,
        breachesRes,
        reportsRes,
        fundsRes,
        summaryRes
      ] = await Promise.all([
        api.get('/risk/categories'),
        api.get('/risk/factors'),
        api.get('/risk/profiles'),
        api.get('/risk/scenarios'),
        api.get('/risk/limits'),
        api.get('/risk/breaches'),
        api.get('/risk/reports'),
        api.get('/funds/'),
        api.get('/risk/dashboard/summary')
      ]);

      setCategories(categoriesRes.data || []);
      setFactors(factorsRes.data || []);
      setProfiles(profilesRes.data || []);
      setScenarios(scenariosRes.data || []);
      setLimits(limitsRes.data || []);
      setBreaches(breachesRes.data || []);
      setReports(reportsRes.data || []);
      setFunds(fundsRes.data.funds || []);
      setDashboardSummary(summaryRes.data);

    } catch (err) {
      LoggerService.error('Error loading risk analysis data:', err);
      setError(err && typeof err === 'object' && 'message' in err ? String(err.message) : 'Failed to load risk analysis data');
      enqueueSnackbar('Failed to load risk analysis data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFundAnalysisClick = async (fund: Fund) => {
    try {
      setSelectedFund(fund);
      const response = await api.get(`/risk/funds/${fund.id}/analysis`);
      setFundAnalysisData(response.data);
      setFundAnalysisDialogOpen(true);
    } catch (err) {
      LoggerService.error('Error loading fund analysis:', err);
      enqueueSnackbar('Failed to load fund analysis', { variant: 'error' });
    }
  };

  const getRiskLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'very high': return 'error';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'extreme': return 'error';
      default: return 'default';
    }
  };

  const getBreachSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'low': return 'info';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  // Chart data for risk level distribution
  const riskLevelChartData = dashboardSummary?.risk_level_distribution?.map((item: any) => ({
    name: item.level,
    value: item.count,
    color: getRiskLevelColor(item.level) === 'success' ? '#4caf50' : 
           getRiskLevelColor(item.level) === 'warning' ? '#ff9800' : '#f44336'
  })) || [];

  // Chart data for scenario severity
  const scenarioSeverityData = dashboardSummary?.scenario_severity_distribution?.map((item: any) => ({
    name: item.severity,
    value: item.count,
    color: getSeverityColor(item.severity) === 'success' ? '#4caf50' : 
           getSeverityColor(item.severity) === 'warning' ? '#ff9800' : '#f44336'
  })) || [];

  // Chart data for risk trends (mock data)
  const riskTrendsData = [
    { month: 'Jan', market: 65, credit: 45, operational: 35, liquidity: 55 },
    { month: 'Feb', market: 68, credit: 48, operational: 38, liquidity: 52 },
    { month: 'Mar', market: 72, credit: 42, operational: 42, liquidity: 58 },
    { month: 'Apr', market: 70, credit: 50, operational: 40, liquidity: 60 },
    { month: 'May', market: 75, credit: 45, operational: 45, liquidity: 55 },
    { month: 'Jun', market: 78, credit: 48, operational: 38, liquidity: 62 }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Risk Analysis
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadRiskAnalysisData}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
        Risk Analysis
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Business color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{dashboardSummary?.total_funds || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Funds
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Assessment color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{dashboardSummary?.total_profiles || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Risk Profiles
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Timeline color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{dashboardSummary?.total_scenarios || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Risk Scenarios
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Warning color="error" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6" color="error.main">
                    {dashboardSummary?.total_active_breaches || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Breaches
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Active Breaches Alert */}
      {dashboardSummary?.total_active_breaches > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Active Risk Breaches Require Attention
          </Typography>
          <Typography variant="body2">
            {dashboardSummary.total_active_breaches} risk limit breaches are currently active and require immediate attention.
          </Typography>
        </Alert>
      )}

      {/* Main Content Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Dashboard" icon={<ShowChart />} />
          <Tab label="Risk Profiles" icon={<Assessment />} />
          <Tab label="Risk Factors" icon={<Security />} />
          <Tab label="Scenarios" icon={<Timeline />} />
          <Tab label="Limits & Breaches" icon={<Warning />} />
          <Tab label="Reports" icon={<BarChart />} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Risk Level Distribution */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Risk Level Distribution" />
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={riskLevelChartData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {riskLevelChartData.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Scenario Severity Distribution */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Scenario Severity Distribution" />
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsBarChart data={scenarioSeverityData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="value" fill="#2196f3" />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Risk Trends */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Risk Trends Over Time" />
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={riskTrendsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip />
                    <Area type="monotone" dataKey="market" stackId="1" stroke="#8884d8" fill="#8884d8" name="Market Risk" />
                    <Area type="monotone" dataKey="credit" stackId="1" stroke="#82ca9d" fill="#82ca9d" name="Credit Risk" />
                    <Area type="monotone" dataKey="operational" stackId="1" stroke="#ffc658" fill="#ffc658" name="Operational Risk" />
                    <Area type="monotone" dataKey="liquidity" stackId="1" stroke="#ff7300" fill="#ff7300" name="Liquidity Risk" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Breaches */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Recent Risk Breaches" />
              <CardContent>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Fund</TableCell>
                        <TableCell>Breach Date</TableCell>
                        <TableCell>Severity</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboardSummary?.recent_breaches?.slice(0, 5).map((breach: any) => (
                        <TableRow key={breach.id}>
                          <TableCell>
                            {funds.find(f => f.id === breach.fund_id)?.name || 'Unknown Fund'}
                          </TableCell>
                          <TableCell>{new Date(breach.breach_date).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Chip
                              label={breach.breach_severity}
                              color={getBreachSeverityColor(breach.breach_severity) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{breach.breach_description}</TableCell>
                          <TableCell>
                            <Chip
                              label={breach.is_resolved ? "Resolved" : "Active"}
                              color={breach.is_resolved ? "success" : "error"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton size="small">
                              <OpenInNew />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Card>
          <CardHeader title="Fund Risk Profiles" />
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Fund Name</TableCell>
                    <TableCell>Risk Score</TableCell>
                    <TableCell>Risk Level</TableCell>
                    <TableCell>Assessment Date</TableCell>
                    <TableCell>Methodology</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {profiles.map((profile) => {
                    const fund = funds.find(f => f.id === profile.fund_id);
                    return (
                      <TableRow key={profile.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {fund?.name || 'Unknown Fund'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {fund?.fund_type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Typography variant="body2" sx={{ mr: 1 }}>
                              {profile.risk_score.toFixed(1)}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={profile.risk_score}
                              sx={{ width: 60, height: 8 }}
                              color={getRiskLevelColor(profile.risk_level) as any}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={profile.risk_level}
                            color={getRiskLevelColor(profile.risk_level) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{new Date(profile.assessment_date).toLocaleDateString()}</TableCell>
                        <TableCell>{profile.assessment_methodology}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => fund && handleFundAnalysisClick(fund)}
                          >
                            <OpenInNew />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card>
          <CardHeader 
            title="Risk Factors"
            action={
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            }
          />
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Weight</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {factors
                    .filter(factor => !selectedCategory || factor.category_id === selectedCategory)
                    .map((factor) => {
                      const category = categories.find(c => c.id === factor.category_id);
                      return (
                        <TableRow key={factor.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {factor.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={category?.name || 'Unknown'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={factor.factor_type}
                              size="small"
                              color={factor.factor_type === 'Quantitative' ? 'primary' : 'secondary'}
                            />
                          </TableCell>
                          <TableCell>{factor.measurement_unit}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {(factor.weight * 100).toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {factor.description}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 3 && (
        <Card>
          <CardHeader 
            title="Risk Scenarios"
            action={
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={selectedSeverity}
                  onChange={(e) => setSelectedSeverity(e.target.value)}
                  label="Severity"
                >
                  <MenuItem value="">All Severities</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Extreme">Extreme</MenuItem>
                </Select>
              </FormControl>
            }
          />
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Scenario Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Probability</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scenarios
                    .filter(scenario => !selectedSeverity || scenario.severity === selectedSeverity)
                    .map((scenario) => (
                      <TableRow key={scenario.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {scenario.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={scenario.scenario_type}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={scenario.severity}
                            color={getSeverityColor(scenario.severity) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {(scenario.probability * 100).toFixed(1)}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {scenario.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small">
                            <OpenInNew />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 4 && (
        <Grid container spacing={3}>
          {/* Risk Limits */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Risk Limits" />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fund</TableCell>
                        <TableCell>Factor</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Limit</TableCell>
                        <TableCell>Unit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {limits.slice(0, 10).map((limit) => {
                        const fund = funds.find(f => f.id === limit.fund_id);
                        return (
                          <TableRow key={limit.id}>
                            <TableCell>
                              <Typography variant="caption">
                                {fund?.name || 'Unknown'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {limit.risk_factor?.name || 'Unknown'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={limit.limit_type}
                                size="small"
                                color={limit.limit_type === 'Hard Limit' ? 'error' : 
                                       limit.limit_type === 'Soft Limit' ? 'warning' : 'info'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {limit.limit_value.toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {limit.limit_unit}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Risk Breaches */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Risk Breaches" />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fund</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Severity</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {breaches.slice(0, 10).map((breach) => {
                        const fund = funds.find(f => f.id === breach.fund_id);
                        return (
                          <TableRow key={breach.id}>
                            <TableCell>
                              <Typography variant="caption">
                                {fund?.name || 'Unknown'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {new Date(breach.breach_date).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={breach.breach_severity}
                                color={getBreachSeverityColor(breach.breach_severity) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={breach.is_resolved ? "Resolved" : "Active"}
                                color={breach.is_resolved ? "success" : "error"}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton size="small">
                                <OpenInNew />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 5 && (
        <Card>
          <CardHeader title="Risk Analysis Reports" />
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Report Name</TableCell>
                    <TableCell>Fund</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((report) => {
                    const fund = funds.find(f => f.id === report.fund_id);
                    return (
                      <TableRow key={report.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {report.report_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{fund?.name || 'Unknown Fund'}</TableCell>
                        <TableCell>
                          <Chip
                            label={report.report_type}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{new Date(report.report_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Chip
                            label={report.report_status}
                            color={report.report_status === 'Final' ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small">
                            <OpenInNew />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Fund Analysis Dialog */}
      <Dialog open={fundAnalysisDialogOpen} onClose={() => setFundAnalysisDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Typography variant="h6">
            Risk Analysis - {selectedFund?.name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {fundAnalysisData && (
            <Box>
              {/* Risk Profile Summary */}
              <Card sx={{ mb: 2 }}>
                <CardHeader title="Risk Profile Summary" />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Risk Score</Typography>
                      <Typography variant="h6">{fundAnalysisData.risk_profile?.risk_score?.toFixed(1) || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Risk Level</Typography>
                      <Chip
                        label={fundAnalysisData.risk_profile?.risk_level || 'N/A'}
                        color={getRiskLevelColor(fundAnalysisData.risk_profile?.risk_level) as any}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Assessment Date</Typography>
                      <Typography variant="body2">
                        {fundAnalysisData.risk_profile?.assessment_date ? 
                          new Date(fundAnalysisData.risk_profile.assessment_date).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Methodology</Typography>
                      <Typography variant="body2">{fundAnalysisData.risk_profile?.assessment_methodology || 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Risk Measurements */}
              <Card sx={{ mb: 2 }}>
                <CardHeader title="Risk Measurements" />
                <CardContent>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Risk Factor</TableCell>
                          <TableCell>Value</TableCell>
                          <TableCell>Unit</TableCell>
                          <TableCell>Confidence</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fundAnalysisData.measurements?.map((measurement: any) => (
                          <TableRow key={measurement.id}>
                            <TableCell>{measurement.risk_factor?.name || 'Unknown'}</TableCell>
                            <TableCell>{measurement.measurement_value?.toFixed(2) || 'N/A'}</TableCell>
                            <TableCell>{measurement.risk_factor?.measurement_unit || 'N/A'}</TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {measurement.confidence_level?.toFixed(1) || 'N/A'}%
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {measurement.measurement_date ? 
                                new Date(measurement.measurement_date).toLocaleDateString() : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Risk Limits */}
              <Card sx={{ mb: 2 }}>
                <CardHeader title="Risk Limits" />
                <CardContent>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Risk Factor</TableCell>
                          <TableCell>Limit Type</TableCell>
                          <TableCell>Limit Value</TableCell>
                          <TableCell>Unit</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fundAnalysisData.limits?.map((limit: any) => (
                          <TableRow key={limit.id}>
                            <TableCell>{limit.risk_factor?.name || 'Unknown'}</TableCell>
                            <TableCell>
                              <Chip
                                label={limit.limit_type}
                                size="small"
                                color={limit.limit_type === 'Hard Limit' ? 'error' : 
                                       limit.limit_type === 'Soft Limit' ? 'warning' : 'info'}
                              />
                            </TableCell>
                            <TableCell>{limit.limit_value?.toFixed(2) || 'N/A'}</TableCell>
                            <TableCell>{limit.limit_unit || 'N/A'}</TableCell>
                            <TableCell>{limit.breach_action || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Recent Breaches */}
              <Card>
                <CardHeader title="Recent Breaches" />
                <CardContent>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Severity</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fundAnalysisData.breaches?.map((breach: any) => (
                          <TableRow key={breach.id}>
                            <TableCell>
                              {breach.breach_date ? 
                                new Date(breach.breach_date).toLocaleDateString() : 'N/A'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={breach.breach_severity}
                                color={getBreachSeverityColor(breach.breach_severity) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{breach.breach_description || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={breach.is_resolved ? "Resolved" : "Active"}
                                color={breach.is_resolved ? "success" : "error"}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFundAnalysisDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RiskAnalysisPage;
